// Centre — the workspace centre: the active tab's body, and the recursive
// PaneView when the workspace is split. Extracted from AppShellHost's render
// tree (C1 #9); every fact + intent comes off the AppShellModel context.
//
// A single-leaf tree renders the active tab's `materialSlot`; a SPLIT renders
// the recursive pane layout (each leaf its own strip + body, branches flex
// rows/columns with a Sash between siblings). The FOCUSED pane reuses the full
// slot; non-focused panes get a light body (chat / settings render; a
// non-focused resource shows a calm placeholder).
//
// Each tab kind's centre lives in its own module under `Centre/`
// (module-topology split); this entry keeps the tab switch + pane layout.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { Fragment, type ReactNode } from "react";
import { Button } from "../../components/Button/Button.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import type { Leaf, Pane } from "../../components/Navigation/paneTree.js";
import { Sash } from "../../components/PaneView/Sash.js";
import { ConversationCentre } from "./Centre/ConversationCentre.js";
import { HomeCentre } from "./Centre/HomeCentre.js";
import { KnowledgeCentre } from "./Centre/KnowledgeCentre.js";
import { ExportCentre } from "./Centre/MaterialCentre.js";
import { PaneStrip } from "./Centre/PaneStrip.js";
import { ProjectsCentre } from "./Centre/ProjectsCentre.js";
import { QualityTabCentre } from "./Centre/QualityTabCentre.js";
import { SearchTabCentre } from "./Centre/SearchTabCentre.js";
import { SettingsCentre } from "./Centre/SettingsCentre.js";
import { StudioCentre } from "./Centre/StudioCentre.js";
import { spinnerSlot, withPaneBoundary } from "./Centre/slots.js";
import { useAppShell } from "./context.js";
import { openResourceTransition } from "./resourceHome.js";
import { centeredStyle } from "./styles.js";

export const Centre = (): ReactNode => {
    const { t } = useLingui();
    const { frame, dispatch, activeRef, isSplit, tabs, refFor } = useAppShell();
    const { paneTree } = frame;

    const materialSlot: ReactNode = (() => {
        switch (tabs.active.type) {
            case "conversation":
                // The conversation owns the centre as a REAL tab (D3), rendering
                // the hero composer when empty and the transcript once a message
                // lands — one tab, derived state.
                return <ConversationCentre conversationId={tabs.active.id} />;
            case "settings":
                // Settings is a singleton TAB (P-CHROME step 4) — it
                // renders in the centre like any tab, keeping rail/dock.
                return <SettingsCentre />;
            case "home":
                // Home is a singleton TAB (dogfood F1) — the workspace overview.
                return <HomeCentre />;
            case "studio":
                // Studio is a singleton TAB (Studio v0) — the canvas.
                return <StudioCentre />;
            case "projects":
                // The Projects list singleton TAB (#161).
                return <ProjectsCentre />;
            case "knowledge":
                // A source gone into (its reader); the list itself is
                // Search scoped to Sources.
                return (
                    <KnowledgeCentre
                        selectedId={tabs.active.selectedId}
                        target={tabs.active.target}
                    />
                );
            case "search":
                // The one Search page (pen "One search"): its scope is the
                // tab's facet, its words the tab's query (`?q=`).
                return (
                    <SearchTabCentre
                        kind={tabs.active.kind}
                        query={tabs.active.query ?? ""}
                        selectedId={tabs.active.selectedId}
                        target={tabs.active.target}
                    />
                );
            case "quality":
                // The full-quality report as its own tab (owner
                // decision) — the standalone FullQualityView drill-down.
                return <QualityTabCentre materialId={tabs.active.materialId} />;
            case "resource":
                // A resource tab whose ref has not resolved yet (cold load /
                // just-deleted) shows a spinner while the load lands or the
                // reconcile drops the tab — never a stray landing.
                if (activeRef === null) return spinnerSlot(t`Loading…`);
                // Only a PACKAGE opens as a tab. A material lives in the
                // library detail (the material tab retired 2026-10-01), a
                // source or file in the Knowledge bank (#59): a ref of those
                // kinds reaching here is a legacy restored tab, so point to
                // its real home (the one routing rule, `resourceHome`).
                if (activeRef.type !== "export") {
                    const home = openResourceTransition(activeRef);
                    return (
                        <div style={centeredStyle}>
                            <EmptyState
                                title={
                                    activeRef.type === "material"
                                        ? t`This material is in Created materials`
                                        : t`This source is in Uploaded sources`
                                }
                            >
                                <Button
                                    variant="primary"
                                    onClick={() => dispatch(home)}
                                >
                                    {activeRef.type === "material"
                                        ? t`Open in Created materials`
                                        : t`Open in Uploaded sources`}
                                </Button>
                            </EmptyState>
                        </div>
                    );
                }
                return <ExportCentre exportRef={activeRef} />;
            default:
                return exhaustSwitchCase(tabs.active);
        }
    })();

    const lightBody = (leaf: Leaf): ReactNode => {
        switch (leaf.active.type) {
            case "conversation":
                return <ConversationCentre conversationId={leaf.active.id} />;
            case "settings":
                return <SettingsCentre />;
            case "home":
                return <HomeCentre />;
            case "studio":
                return <StudioCentre />;
            case "projects":
                return <ProjectsCentre />;
            case "knowledge":
                return <KnowledgeCentre />;
            case "search":
                // The Library's query and opened row are the FOCUSED Search
                // tab's (URL-mirrored); a non-focused pane only names it.
                return (
                    <div className="bilda-pane__placeholder">
                        {t`Focus this pane to search`}
                    </div>
                );
            case "quality":
                // The report reads its evaluation from the per-material cache by
                // id, so a non-focused pane renders it the same as the focused
                // one (no per-pane material load needed).
                return <QualityTabCentre materialId={leaf.active.materialId} />;
            case "resource":
                return (
                    <div className="bilda-pane__placeholder">
                        {t`Focus this pane to open ${refFor(leaf.active.id)?.title ?? ""}`}
                    </div>
                );
            default:
                return exhaustSwitchCase(leaf.active);
        }
    };

    const renderPane = (pane: Pane): ReactNode => {
        if (pane.type === "leaf") {
            const focused = pane.id === paneTree.focused;
            const a = pane.active;
            const closeId = a.type === "resource" ? a.id : null;
            const resetKey = a.type === "resource" ? `r:${a.id}` : a.type;
            return (
                <div
                    className="bilda-pane"
                    data-focused={focused ? "true" : undefined}
                >
                    <div className="bilda-pane__strip">
                        <PaneStrip leaf={pane} />
                    </div>
                    <div className="bilda-pane__body">
                        {withPaneBoundary(
                            closeId,
                            resetKey,
                            focused ? materialSlot : lightBody(pane),
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div
                className={`bilda-pane-branch bilda-pane-branch--${pane.orientation}`}
            >
                {pane.children.map((child, i) => (
                    <Fragment key={child.id}>
                        {i > 0 ? (
                            <Sash
                                orientation={pane.orientation}
                                fraction={pane.sizes[i - 1] ?? 0.5}
                                onResizeBy={(delta) =>
                                    dispatch({
                                        type: "resize-divider",
                                        branch: pane.id,
                                        dividerIndex: i - 1,
                                        delta,
                                    })
                                }
                                onReset={() =>
                                    dispatch({
                                        type: "resize-divider",
                                        branch: pane.id,
                                        dividerIndex: i - 1,
                                        delta: 0.5 - (pane.sizes[i - 1] ?? 0.5),
                                    })
                                }
                            />
                        ) : null}
                        <div
                            className="bilda-pane-branch__child"
                            style={{
                                flexBasis: `${(pane.sizes[i] ?? 0) * 100}%`,
                            }}
                        >
                            {renderPane(child)}
                        </div>
                    </Fragment>
                ))}
            </div>
        );
    };

    // The material slot: the recursive PaneView when split (each leaf wraps its
    // own boundary inside renderPane), else the single leaf's body wrapped in a
    // per-pane boundary (D2) so a render throw fails IN the centre, not across
    // the whole app.
    return isSplit
        ? renderPane(paneTree.root)
        : withPaneBoundary(
              activeRef?.id ?? null,
              activeRef?.id ?? "centre",
              materialSlot,
          );
};
